import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export const ChatSupport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Hello! Welcome to SolarBull. How can I help you today?", 
      sender: "bot",
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');

  const quickReplies = [
    "Get a quote",
    "Installation time",
    "Subsidy information",
    "Talk to expert"
  ];

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    const newMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages([...messages, newMessage]);
    setInputMessage('');

    // Mock bot response
    setTimeout(() => {
      const botResponse = {
        id: messages.length + 2,
        text: "Thank you for your message! Our team will get back to you shortly. For immediate assistance, please call us at +91 9553746982.",
        sender: 'bot',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  const handleQuickReply = (reply) => {
    const newMessage = {
      id: messages.length + 1,
      text: reply,
      sender: 'user',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages([...messages, newMessage]);

    // Mock response based on quick reply
    setTimeout(() => {
      let responseText = "";
      if (reply === "Get a quote") {
        responseText = "I'd be happy to help you get a quote! Please visit our Solar Calculator page or fill out the contact form with your requirements.";
      } else if (reply === "Installation time") {
        responseText = "Residential installations typically take 1-2 weeks, commercial and industrial projects take 2-16 weeks depending on capacity and complexity.";
      } else if (reply === "Subsidy information") {
        responseText = "Government subsidies for residential: 1kW - ₹30,000, 2kW - ₹60,000, 3kW and above - ₹78,000. We'll assist you with the application!";
      } else if (reply === "Back to menu") {
        responseText = "Here are the quick options again:";
      } else {
        responseText = "I'll connect you with our solar expert. Please call us at +91 9553746982 or share your contact details.";
      }

      const botResponse = {
        id: messages.length + 2,
        text: responseText,
        sender: 'bot',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-28 right-6 z-50 p-4 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 ${
          isOpen 
            ? 'bg-gray-800 hover:bg-gray-900' 
            : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
        }`}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-44 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
            <h3 className="font-semibold text-lg">SolarBull Support</h3>
            <p className="text-xs text-orange-100">We typically reply in a few minutes</p>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 space-y-4 overflow-y-auto h-96 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${message.sender === 'user' ? 'order-2' : 'order-1'}`}>
                  <div
                    className={`rounded-2xl px-4 py-2 ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                  </div>
                  <p className={`text-xs text-gray-500 mt-1 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                    {message.time}
                  </p>
                </div>
              </div>
            ))}

            {/* Quick Replies */}
            {(messages.length <= 2 || messages[messages.length - 1].text === "Here are the quick options again:") && (
              <div className="pt-2">
                <p className="text-xs text-gray-500 mb-2">Quick replies:</p>
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      className="px-3 py-1.5 bg-white border border-orange-300 text-orange-600 rounded-full text-xs hover:bg-orange-50 transition-colors duration-300"
                    >
                      {reply}
                    </button>
                  ))}
                  {messages.length > 2 && (
                    <button
                      onClick={() => handleQuickReply("Back to menu")}
                      className="px-3 py-1.5 bg-orange-500 text-white rounded-full text-xs hover:bg-orange-600 transition-colors duration-300"
                    >
                      Back to menu
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {messages.length > 2 && messages[messages.length - 1].text !== "Here are the quick options again:" && (
              <div className="pt-2">
                <button
                  onClick={() => handleQuickReply("Back to menu")}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors duration-300 w-full"
                >
                  ← Back to main menu
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex space-x-2">
              <Input
                type="text"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                className="flex-1 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
              <Button
                onClick={handleSendMessage}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
